import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushNotifications } from '@capacitor/push-notifications';
import { getPlatform, isNative } from './capacitor';

export const isLikelyApnsToken = (tokenValue) =>
  typeof tokenValue === 'string' && /^[a-f0-9]{64}$/i.test(tokenValue.trim());

async function resolveFcmToken() {
  try {
    const tokenResp = await FirebaseMessaging.getToken();
    return tokenResp?.token || null;
  } catch {
    return null;
  }
}

let refreshListenerAdded = false;

/**
 * Listen for token refresh after initial registration (native only).
 * @param {(token: string) => void | Promise<void>} onToken
 */
export function setupPushTokenRefreshListener(onToken) {
  if (!isNative() || refreshListenerAdded) return;
  refreshListenerAdded = true;

  PushNotifications.addListener('registration', async ({ value }) => {
    if (!value) return;
    if (getPlatform() === 'ios' && isLikelyApnsToken(value)) {
      const fcm = await resolveFcmToken();
      if (fcm) onToken(fcm);
      return;
    }
    onToken(value);
  });

  FirebaseMessaging.addListener('tokenReceived', async (event) => {
    if (event?.token) onToken(event.token);
  });
}

/**
 * Request permission and return the device FCM token.
 * Resolves to null on web or when permission is denied.
 * Must NOT block the initial render — call this after first paint.
 */
export async function registerAndGetToken() {
  if (!isNative()) return null;

  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== 'granted') return null;

  await PushNotifications.register();

  const fcmFromSdk = await resolveFcmToken();
  if (fcmFromSdk && !isLikelyApnsToken(fcmFromSdk)) return fcmFromSdk;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (token) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };

    const timer = setTimeout(() => finish(null), 12000);

    PushNotifications.addListener('registration', async ({ value }) => {
      clearTimeout(timer);
      if (!value) {
        finish(null);
        return;
      }
      if (getPlatform() === 'ios' && isLikelyApnsToken(value)) {
        const fcm = await resolveFcmToken();
        finish(fcm && !isLikelyApnsToken(fcm) ? fcm : null);
        return;
      }
      finish(value);
    });

    PushNotifications.addListener('registrationError', () => {
      clearTimeout(timer);
      finish(null);
    });
  });
}
